import { create } from 'zustand';
import { IListState } from './types';
import { listApi } from '@/API';
import { convertKeys } from '@/utils/common';
import { findCurrentOffsetItem } from '@/utils/convertDate';
import dayjs from 'dayjs';

export const useListStore = create<IListState>((set, get) => ({
  setState: (value) => set({ ...value }),
  loading: false,
  timezones: [],
  countries: [],

  changeField: (state, field) => set(() => ({ [field]: state })),

  countriesRequest: async (request) => {
    if (get().countries.length) return;
    set({ loading: true });
    try {
      const response = await request();
      const {
        data: { data, success },
        error,
      } = response;
      if (error || !success) {
        console.error('Failed to fetch countries:', error);
        return;
      }
      set({ countries: convertKeys(data) });
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  getCountries: async () =>
    get().countriesRequest(() => listApi.getListCountries()),

  timezonesRequest: async (request) => {
    if (get().timezones.length) return;
    set({ loading: true });
    try {
      const response = await request();
      const {
        data: { data, success },
        error,
      } = response;
      if (error || !success) {
        console.error('Failed to fetch timezones:', error);
        return;
      }
      set({ timezones: convertKeys(data) });
    } catch (error) {
      console.error('Failed to fetch timezones:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  getTimezoneById: (timezoneId: string) => {
    return get().timezones.find(({ id }) => id === timezoneId) ?? null;
  },

  getOffsetByTimezoneId: (timezoneId, date = dayjs()) => {
    // The offset in force at the date, as it differs on either side of a DST change.
    const convertDate = dayjs(date);
    const findOffsets = get().timezones?.find(
      ({ id }) => id === timezoneId,
    )?.offsets;
    if (!findOffsets) return null;
    return findCurrentOffsetItem(findOffsets, convertDate);
  },

  getTimezones: async () =>
    get().timezonesRequest(() => listApi.getListTimezones()),

  getTimezonesByKiosk: async () =>
    get().timezonesRequest(() => listApi.getListTimezonesByKiosk()),
}));
